import React, { FC, useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

type AutoFitTextProps = {
  text: string;
  /** Taille max (px). */
  maxPx: number;
  /** Taille min (px). */
  minPx: number;
  fontFamily: string;
  fontWeight: number;
  color: string;
  letterSpacing?: number | string;
  lineHeight?: number;
};

/**
 * Réduit la taille de police jusqu’à ce que le texte tienne sur une ligne
 * (pas d’ellipsis) — adapté à l’affichage TV.
 */
const AutoFitText: FC<AutoFitTextProps> = ({
  text,
  maxPx,
  minPx,
  fontFamily,
  fontWeight,
  color,
  letterSpacing = -0.4,
  lineHeight = 1.05,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = useState(maxPx);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const fit = () => {
      const available = container.clientWidth;
      if (available <= 0) return;

      let size = maxPx;
      measure.style.fontSize = `${size}px`;
      // Boucle décroissante simple (assez de pas pour la TV)
      while (size > minPx && measure.scrollWidth > available) {
        size -= 1;
        measure.style.fontSize = `${size}px`;
      }
      setFontSize(size);
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(container);
    return () => ro.disconnect();
  }, [text, maxPx, minPx, fontFamily, fontWeight, letterSpacing]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Mesure hors flux */}
      <Box
        component="span"
        ref={measureRef}
        aria-hidden
        sx={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          fontFamily,
          fontWeight,
          letterSpacing,
          lineHeight,
        }}
      >
        {text}
      </Box>
      <Box
        component="span"
        sx={{
          display: "block",
          whiteSpace: "nowrap",
          fontFamily,
          fontWeight,
          fontSize,
          letterSpacing,
          lineHeight,
          color,
        }}
      >
        {text}
      </Box>
    </Box>
  );
};

export default AutoFitText;
